import styled, { keyframes } from 'styled-components'
import { Link } from 'react-router-dom'
import breakpoints from '../../config/settings/breakpoints'

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const revealUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const BrandLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
  color: inherit;
`

export const BrandName = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media ${breakpoints.device.sm} {
    font-size: 1.125rem;
  }
`

export const NavLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color 0.15s ease;
  padding: 0.35rem 0;

  &:hover {
    color: ${({ theme }) => theme.colors.action.primary.bgActive};
  }
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
`

export const Hero = styled.section`
  display: grid;
  gap: 1.75rem;
  max-width: 1120px;
  margin: 0 auto;
  padding: 1.75rem 1rem 2.5rem;
  overflow: hidden;

  @media ${breakpoints.device.sm} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
    align-items: end;
    gap: 1.5rem;
    max-width: none;
    padding: 3.5rem 0 4rem clamp(1.5rem, 6vw, calc((100vw - 1120px) / 2 + 1.5rem));
    min-height: calc(100vh - 72px);
  }
`

export const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  padding-bottom: 0;
  animation: ${fadeUp} 0.55s ease-out both;

  @media ${breakpoints.device.sm} {
    gap: 1rem;
    max-width: 480px;
    padding-bottom: 3.5rem;
  }
`

export const HeroBrand = styled.p`
  margin: 0;
  font-size: clamp(1.875rem, 8vw, 3.25rem);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const HeroHeadline = styled.h1`
  margin: 0;
  max-width: 24ch;
  font-size: clamp(1.25rem, 5.5vw, 2rem);
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.colors.textPrimary};
  animation: ${fadeUp} 0.55s 0.08s ease-out both;
`

export const HeroSupport = styled.p`
  margin: 0;
  max-width: 40ch;
  font-size: 0.975rem;
  font-weight: 400;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  animation: ${fadeUp} 0.55s 0.14s ease-out both;

  @media ${breakpoints.device.sm} {
    font-size: 1.0625rem;
  }
`

export const CtaGroup = styled.div`
  display: flex;
  width: 100%;
  margin-top: 0.35rem;
  animation: ${fadeUp} 0.55s 0.2s ease-out both;

  @media ${breakpoints.device.sm} {
    width: auto;
    margin-top: 0.5rem;
  }
`

export const PrimaryCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 0.875rem 1.35rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.action.primary.bg};
  background: ${({ theme }) => theme.colors.action.primary.bg};
  color: ${({ theme }) => theme.colors.action.primary.text};
  font-family: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;

  @media ${breakpoints.device.sm} {
    width: auto;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.action.primary.bgHover};
    border-color: ${({ theme }) => theme.colors.action.primary.bgActive};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

export const HeroVisual = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: flex-end;
  margin: 0 -1rem;
  padding: 0 1rem;
  animation: ${revealUp} 0.7s 0.18s ease-out both;

  @media ${breakpoints.device.sm} {
    justify-content: flex-end;
    margin: 0;
    padding: 0;

    & > * {
      max-width: none;
      width: min(100%, 680px);
      border-radius: 16px 0 0 16px;
      border-right: none;
    }
  }
`

export const Section = styled.section`
  max-width: 1120px;
  margin: 0 auto;
  padding: 2.75rem 1rem;

  @media ${breakpoints.device.sm} {
    padding: 5rem 1.5rem;
  }
`

export const SectionNarrow = styled(Section)`
  max-width: 720px;
`

export const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: clamp(1.35rem, 5vw, 2rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media ${breakpoints.device.sm} {
    margin-bottom: 1rem;
  }
`

export const SectionBody = styled.p`
  margin: 0;
  font-size: 0.975rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media ${breakpoints.device.sm} {
    font-size: 1.0625rem;
  }
`

export const Steps = styled.ol`
  list-style: none;
  margin: 1.5rem 0 0;
  padding: 0;
  display: grid;
  gap: 1.35rem;
  counter-reset: step;

  @media ${breakpoints.device.sm} {
    margin-top: 2rem;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
`

export const Step = styled.li`
  counter-increment: step;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  &::before {
    content: counter(step, decimal-leading-zero);
    font-size: 0.8125rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.action.primary.bgActive};
  }
`

export const StepTitle = styled.h3`
  margin: 0;
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.textPrimary};

  @media ${breakpoints.device.sm} {
    font-size: 1.125rem;
  }
`

export const StepBody = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media ${breakpoints.device.sm} {
    font-size: 0.9375rem;
  }
`

export const ProofSection = styled(Section)`
  display: grid;
  gap: 1.75rem;

  @media ${breakpoints.device.sm} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: center;
    gap: 3rem;
  }
`

export const FeatureList = styled.ul`
  list-style: none;
  margin: 1.25rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  @media ${breakpoints.device.sm} {
    margin-top: 1.5rem;
  }
`

export const FeatureItem = styled.button`
  appearance: none;
  width: 100%;
  margin: 0;
  padding: 0.85rem 0.95rem;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? theme.colors.action.primary.bg : theme.colors.borderDefault};
  border-radius: 12px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.1)'
        : 'rgba(62, 207, 142, 0.08)'
      : theme.colors.background};
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.5;
  text-align: left;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
  box-shadow: ${({ $active }) =>
    $active ? '0 0 0 3px rgba(62, 207, 142, 0.18)' : 'none'};

  strong {
    display: block;
    margin-bottom: 0.15rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    transform: translateY(-1px);
  }

  @media ${breakpoints.device.sm} {
    font-size: 1rem;
  }
`

export const ProofVisual = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.55s ease-out, transform 0.55s ease-out;

  &[data-visible='true'] {
    opacity: 1;
    transform: translateY(0);
  }

  @media ${breakpoints.device.sm} {
    justify-content: flex-end;
  }

  & > * {
    border-radius: 14px;
    border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  }
`

export const TrustStrip = styled.section`
  display: grid;
  gap: 1rem;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0.5rem 1rem 0.5rem;

  @media ${breakpoints.device.sm} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
    padding: 1.5rem 1.5rem 0;
  }
`

export const TrustItem = styled.div`
  padding: 1rem 1.05rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
`

export const TrustItemTitle = styled.h3`
  margin: 0 0 0.35rem;
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const TrustItemBody = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const TrustNote = styled.p`
  margin: 1rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};

  @media ${breakpoints.device.sm} {
    margin-top: 1.25rem;
  }
`

export const FaqList = styled.div`
  margin: 1.5rem 0 0;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};

  @media ${breakpoints.device.sm} {
    margin-top: 2rem;
  }
`

export const FaqItem = styled.details`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};

  &[open] summary::after {
    transform: rotate(-135deg);
  }
`

export const FaqQuestion = styled.summary`
  list-style: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0;
  padding: 1rem 0;
  font-size: 0.975rem;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.015em;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '';
    flex-shrink: 0;
    width: 0.55rem;
    height: 0.55rem;
    margin-top: -0.2rem;
    border-right: 2px solid ${({ theme }) => theme.colors.textMuted};
    border-bottom: 2px solid ${({ theme }) => theme.colors.textMuted};
    transform: rotate(45deg);
    transition: transform 0.15s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.action.primary.bgActive};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 4px;
    border-radius: 4px;
  }

  @media ${breakpoints.device.sm} {
    padding: 1.15rem 0;
    font-size: 1.0625rem;
  }
`

export const FaqAnswer = styled.p`
  margin: 0;
  padding: 0 0 1.15rem;
  max-width: 58ch;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media ${breakpoints.device.sm} {
    padding-bottom: 1.35rem;
    font-size: 0.975rem;
  }
`

export const FinalCta = styled.section`
  max-width: 1120px;
  margin: 0 auto;
  padding: 2.5rem 1rem 3rem;
  text-align: center;

  @media ${breakpoints.device.sm} {
    padding: 4.5rem 1.5rem 5rem;
  }
`

export const FinalTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: clamp(1.35rem, 5vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const FinalSupport = styled.p`
  margin: 0 auto 1.25rem;
  max-width: 36ch;
  font-size: 0.975rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};

  @media ${breakpoints.device.sm} {
    margin-bottom: 1.5rem;
    font-size: 1.0625rem;
  }
`

export const FinalCtaGroup = styled.div`
  display: flex;
  justify-content: stretch;
  width: 100%;
  max-width: 360px;
  margin: 0 auto;

  @media ${breakpoints.device.sm} {
    justify-content: center;
    max-width: none;
    width: auto;
  }
`

export const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const FooterBrandRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
`

export const FooterText = styled.p`
  margin: 0;
  max-width: 42ch;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;

  @media ${breakpoints.device.sm} {
    align-items: flex-end;
  }
`
